import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import { MedicalBackgroundQuestionnaireItem } from 'components/Assessments/FuaAssessment/sections/MedicalBackground/questionnaire'
import { IntegumentaryFormField } from './formField'

/**
 * Integumentary subsection adapter
 * Handles skin assessment, wounds, and nail condition (6 fields)
 */
export class IntegumentaryAdapter extends BaseSubsectionAdapter<FuaFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<FuaFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Integumentary system assessment fields
    const skinColor = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_COLOR
    )?.at(0)

    const skinIssues = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_ISSUES
    )

    const nails = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_NAILS
    )?.at(0)

    const identifiedWounds = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_IDENTIFIED_WOUNDS
    )?.at(0)

    const woundNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_WOUND_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [IntegumentaryFormField.INTEGUMENTARY_SKIN_COLOR]: skinColor,
      [IntegumentaryFormField.INTEGUMENTARY_SKIN_ISSUES]: skinIssues,
      [IntegumentaryFormField.INTEGUMENTARY_NAILS]: nails,
      [IntegumentaryFormField.INTEGUMENTARY_IDENTIFIED_WOUNDS]: identifiedWounds,
      [IntegumentaryFormField.INTEGUMENTARY_WOUND_NOTES]: woundNotes,
      [IntegumentaryFormField.INTEGUMENTARY_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_COLOR,
        value: this.getFormDataField(IntegumentaryFormField.INTEGUMENTARY_SKIN_COLOR)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_ISSUES,
        value: this.getFormDataField(IntegumentaryFormField.INTEGUMENTARY_SKIN_ISSUES)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_NAILS,
        value: this.getFormDataField(IntegumentaryFormField.INTEGUMENTARY_NAILS)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_IDENTIFIED_WOUNDS,
        value: this.getFormDataField(IntegumentaryFormField.INTEGUMENTARY_IDENTIFIED_WOUNDS)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SYSTEM_SUMMARY,
        value: this.getFormDataField(IntegumentaryFormField.INTEGUMENTARY_SYSTEM_SUMMARY)
      }
    ]

    // Conditionally add wound notes if they exist (they are conditionally rendered when wounds are identified)
    if (this.formDataFieldExists(IntegumentaryFormField.INTEGUMENTARY_WOUND_NOTES)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_WOUND_NOTES,
        value: this.getFormDataField(IntegumentaryFormField.INTEGUMENTARY_WOUND_NOTES)
      })
    }

    return mutations
  }
}
